/**
 * Team Chat - internal firm messaging for staff. Shows the firm-wide "Everyone"
 * channel and 1:1 DMs (including with the manager). Messages the manager sends
 * from the Manager app appear here, so employees see them in their panel.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Users, Send, Search, Paperclip, Mic, Square, Loader2, X, FileText, Megaphone, MessageSquare, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  getStaffInbox, getStaffThread, sendStaffMessage, uploadStaffAttachment,
  StaffConversation,
} from '@/services/api/staff-chat';
import { blobToWav } from '@/lib/audio';

type Selection = { type: 'everyone' } | { type: 'dm'; peerUserId: string; name: string };

function timeLabel(iso?: string | null) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ', ' + d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export default function TeamMessagesPage() {
  const qc = useQueryClient();
  const [selection, setSelection] = useState<Selection>({ type: 'everyone' });
  const [search, setSearch] = useState('');
  const [showRoster, setShowRoster] = useState(false);

  const { data: inbox } = useQuery({
    queryKey: ['staff-inbox'],
    queryFn: getStaffInbox,
    refetchInterval: 5000,
  });

  const me = inbox?.me;
  const existingPeerIds = new Set((inbox?.conversations || []).map((c) => c.peerUserId));
  const rosterForNew = (inbox?.staff || []).filter((s) => !existingPeerIds.has(s.userId));

  // On mobile we show one pane at a time: the list, then the open thread.
  const [threadOpen, setThreadOpen] = useState(false);
  const openThread = (sel: Selection) => { setSelection(sel); setThreadOpen(true); };

  return (
    <div className="-m-4 flex h-[calc(100dvh-2.75rem)] overflow-hidden md:-m-6 lg:h-[calc(100dvh-5.5rem)]">
      {/* Conversations rail (full-width on mobile, fixed rail on desktop) */}
      <aside className={cn('w-full shrink-0 flex-col border-r border-border bg-card lg:flex lg:w-72',
        threadOpen ? 'hidden' : 'flex')}>
        <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Users className="h-4 w-4" /> Team Chat
          </h2>
          <button
            onClick={() => setShowRoster((v) => !v)}
            className="rounded-md px-2 py-1 text-xs font-medium text-primary hover:bg-muted"
          >
            {showRoster ? 'Done' : '+ New'}
          </button>
        </div>

        {showRoster ? (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="border-b border-border p-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search staff..."
                  className="w-full rounded-md border border-border bg-background py-1.5 pl-7 pr-2 text-sm outline-none focus:border-primary"
                />
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {rosterForNew
                .filter((s) => s.name.toLowerCase().includes(search.toLowerCase()))
                .map((s) => (
                  <button
                    key={s.userId}
                    onClick={() => { openThread({ type: 'dm', peerUserId: s.userId, name: s.name }); setShowRoster(false); setSearch(''); }}
                    className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm hover:bg-muted"
                  >
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">
                      {s.name.slice(0, 2).toUpperCase()}
                    </span>
                    <span className="truncate text-foreground">{s.name}</span>
                  </button>
                ))}
              {rosterForNew.length === 0 && (
                <p className="px-4 py-4 text-xs text-muted-foreground">No other staff in this firm yet.</p>
              )}
            </div>
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto">
            {/* Everyone channel */}
            <ConvRow
              active={selection.type === 'everyone'}
              icon={<Megaphone className="h-4 w-4" />}
              title="Everyone"
              preview={inbox?.everyone.lastBody || 'Firm-wide channel'}
              time={inbox?.everyone.lastAt}
              unread={inbox?.everyone.unread || 0}
              onClick={() => openThread({ type: 'everyone' })}
            />
            {(inbox?.conversations || []).map((c: StaffConversation) => (
              <ConvRow
                key={c.peerUserId}
                active={selection.type === 'dm' && selection.peerUserId === c.peerUserId}
                avatar={c.name.slice(0, 2).toUpperCase()}
                title={c.name}
                preview={c.lastBody || ''}
                time={c.lastAt}
                unread={c.unread}
                onClick={() => openThread({ type: 'dm', peerUserId: c.peerUserId, name: c.name })}
              />
            ))}
            {(inbox?.conversations || []).length === 0 && (
              <p className="px-4 py-4 text-xs text-muted-foreground">
                No direct messages yet. Use <span className="font-medium text-primary">+ New</span> to message a colleague.
              </p>
            )}
          </div>
        )}
      </aside>

      {/* Thread (hidden on mobile until a conversation is opened) */}
      <Thread
        key={selection.type === 'dm' ? selection.peerUserId : 'everyone'}
        selection={selection}
        me={me}
        open={threadOpen}
        onBack={() => setThreadOpen(false)}
        onSent={() => qc.invalidateQueries({ queryKey: ['staff-inbox'] })}
      />
    </div>
  );
}

function ConvRow({ active, icon, avatar, title, preview, time, unread, onClick }: {
  active: boolean; icon?: React.ReactNode; avatar?: string; title: string;
  preview: string; time?: string | null; unread: number; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn('flex w-full items-center gap-3 border-b border-border/50 px-4 py-3 text-left transition-colors',
        active ? 'bg-primary/10' : 'hover:bg-muted')}
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">
        {icon || avatar}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center justify-between gap-2">
          <span className="truncate text-sm font-medium text-foreground">{title}</span>
          <span className="shrink-0 text-[10px] text-muted-foreground">{timeLabel(time)}</span>
        </span>
        <span className="flex items-center justify-between gap-2">
          <span className="truncate text-xs text-muted-foreground">{preview}</span>
          {unread > 0 && (
            <span className="flex h-4 min-w-[16px] shrink-0 items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </span>
      </span>
    </button>
  );
}

function Thread({ selection, me, open, onBack, onSent }: { selection: Selection; me?: string; open: boolean; onBack: () => void; onSent: () => void }) {
  const qc = useQueryClient();
  const peerId = selection.type === 'dm' ? selection.peerUserId : undefined;
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Keep the first signed URL per message so the 4s poll doesn't swap an
  // <audio src> mid-playback (re-signed URLs would otherwise restart the clip).
  const urlCacheRef = useRef<Map<string, string>>(new Map());
  const stableUrl = (id: string, url: string) => {
    const cached = urlCacheRef.current.get(id);
    if (cached) return cached;
    urlCacheRef.current.set(id, url);
    return url;
  };

  const { data, isLoading } = useQuery({
    queryKey: ['staff-thread', peerId || 'everyone'],
    queryFn: () => getStaffThread(peerId),
    refetchInterval: 4000,
  });
  const messages = data?.messages || [];
  const myId = me || data?.me;

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, peerId]);

  // Only one audio plays at a time.
  const onAudioPlay = (e: React.SyntheticEvent<HTMLAudioElement>) => {
    const el = scrollRef.current; if (!el) return;
    el.querySelectorAll('audio').forEach((a) => { if (a !== e.currentTarget) a.pause(); });
  };

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['staff-thread', peerId || 'everyone'] });
    onSent();
  };

  const doSend = async (attachment?: { s3Key: string; filename: string; contentType: string }) => {
    const body = text.trim();
    if (!body && !attachment) return;
    setSending(true);
    try {
      await sendStaffMessage({ recipientUserId: peerId || null, body, attachment });
      setText('');
      refresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setSending(true);
    try {
      const att = await uploadStaffAttachment(file, file.name);
      await doSend(att);
    } catch (err: any) {
      toast.error(err?.message || 'Upload failed');
    } finally {
      setSending(false);
    }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (ev) => { if (ev.data.size) chunksRef.current.push(ev.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (timerRef.current) clearInterval(timerRef.current);
        const raw = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
        setSending(true);
        try {
          // Re-encode to WAV so the clip has a real duration and plays in full
          // (and on Safari/iOS) in both the panel and the Manager app. Fall back
          // to the raw recording if decoding isn't supported.
          let blob = raw, ext = 'wav';
          try { blob = await blobToWav(raw); }
          catch { ext = (mr.mimeType || 'audio/webm').includes('mp4') ? 'mp4' : 'webm'; }
          const att = await uploadStaffAttachment(blob, `voice-note.${ext}`);
          await doSend(att);
        } catch (err: any) {
          toast.error(err?.message || 'Voice upload failed');
        } finally {
          setSending(false);
        }
      };
      recRef.current = mr;
      mr.start();
      setRecording(true);
      setRecSecs(0);
      timerRef.current = setInterval(() => setRecSecs((s) => s + 1), 1000);
    } catch {
      toast.error('Microphone not available');
    }
  };
  const stopRec = () => { recRef.current?.stop(); setRecording(false); };
  const cancelRec = () => {
    const mr = recRef.current;
    if (mr) { mr.onstop = null as any; mr.stop(); (mr.stream as MediaStream)?.getTracks().forEach((t) => t.stop()); }
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false);
  };
  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const title = selection.type === 'everyone' ? 'Everyone' : selection.name;

  return (
    <section className={cn('min-w-0 flex-1 flex-col bg-background lg:flex', open ? 'flex' : 'hidden')}>
      <header className="flex items-center gap-2 border-b border-border px-3 py-3 sm:px-5">
        <button onClick={onBack} className="-ml-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted lg:hidden" aria-label="Back to conversations">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
          {selection.type === 'everyone' ? <Megaphone className="h-4 w-4" /> : <span className="text-xs font-semibold">{title.slice(0, 2).toUpperCase()}</span>}
        </span>
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-foreground">{title}</h3>
          <p className="text-xs text-muted-foreground">{selection.type === 'everyone' ? 'Firm-wide channel' : 'Direct message'}</p>
        </div>
      </header>

      <div ref={scrollRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
        {isLoading ? (
          <div className="flex h-full items-center justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-muted-foreground">
            <MessageSquare className="h-8 w-8 opacity-40" />
            <p className="text-sm">No messages yet. Say hello.</p>
          </div>
        ) : messages.map((m) => {
          const mine = m.sender_user_id === myId;
          const isAudio = (m.attachment_type || '').startsWith('audio');
          const isImage = (m.attachment_type || '').startsWith('image');
          return (
            <div key={m.id} className={cn('flex flex-col', mine ? 'items-end' : 'items-start')}>
              <div className={cn('max-w-[70%] rounded-2xl px-4 py-2 text-sm',
                mine ? 'bg-primary text-primary-foreground' : 'bg-card text-foreground border border-border')}>
                {m.body && <p className="whitespace-pre-wrap break-words">{m.body}</p>}
                {m.attachmentUrl && isAudio && (
                  <audio controls preload="none" onPlay={onAudioPlay} src={stableUrl(m.id, m.attachmentUrl)} className="mt-1 h-9 w-56 max-w-full" />
                )}
                {m.attachmentUrl && isImage && (
                  <a href={m.attachmentUrl} target="_blank" rel="noreferrer">
                    <img src={m.attachmentUrl} alt={m.attachment_name || 'image'} className="mt-1 max-h-56 rounded-lg" />
                  </a>
                )}
                {m.attachmentUrl && !isAudio && !isImage && (
                  <a href={m.attachmentUrl} target="_blank" rel="noreferrer"
                    className={cn('mt-1 flex items-center gap-2 text-xs underline', mine ? 'text-primary-foreground' : 'text-primary')}>
                    <FileText className="h-3.5 w-3.5" /> {m.attachment_name || 'Attachment'}
                  </a>
                )}
              </div>
              <span className="mt-0.5 flex items-center gap-1 px-1 text-[10px] text-muted-foreground">
                {!mine && m.sender_name && (
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary/20 text-[8px] font-semibold text-primary">
                    {m.sender_name.split(' ').map((p: string) => p[0]).join('').slice(0, 2).toUpperCase()}
                  </span>
                )}
                {!mine && m.sender_name ? <span className="font-medium text-foreground/80">{m.sender_name}</span> : null}
                <span>{timeLabel(m.created_at)}</span>
              </span>
            </div>
          );
        })}
      </div>

      {/* Composer */}
      <div className="border-t border-border px-4 py-3">
        {recording ? (
          <div className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-2.5">
            <span className="flex items-center gap-2 text-sm text-rose-500">
              <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-rose-500" />
              Recording {Math.floor(recSecs / 60)}:{String(recSecs % 60).padStart(2, '0')}
            </span>
            <span className="flex-1" />
            <button onClick={cancelRec} className="rounded-md p-2 text-muted-foreground hover:bg-muted" title="Cancel"><X className="h-4 w-4" /></button>
            <button onClick={stopRec} className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground" title="Stop & send">
              <Square className="h-3.5 w-3.5" /> Stop &amp; send
            </button>
          </div>
        ) : (
          <div className="flex items-end gap-2">
            <input ref={fileRef} type="file" className="hidden" onChange={onPickFile} />
            <button onClick={() => fileRef.current?.click()} disabled={sending}
              className="rounded-md p-2.5 text-muted-foreground hover:bg-muted disabled:opacity-50" title="Attach file"><Paperclip className="h-4 w-4" /></button>
            <button onClick={startRec} disabled={sending}
              className="rounded-md p-2.5 text-muted-foreground hover:bg-muted disabled:opacity-50" title="Record voice"><Mic className="h-4 w-4" /></button>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); doSend(); } }}
              placeholder={`Message ${title}...`}
              rows={1}
              className="max-h-32 min-h-[42px] flex-1 resize-none rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:border-primary"
            />
            <button onClick={() => doSend()} disabled={sending || !text.trim()}
              className="flex h-[42px] w-[42px] items-center justify-center rounded-xl bg-primary text-primary-foreground disabled:opacity-40" title="Send">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
